import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Router, Layers, Network, Wifi, Home, Info, AlertTriangle, Printer } from 'lucide-react'; // Removed unused icons

// Context and Algorithm Helpers
import { useWirelessAdvisor } from './context/WirelessAdvisorContext';
import { ALGORITHM_TYPES, getAlgorithmName, getAlgorithmDescription } from './utils/algorithms';
import { calculateGeneticPlacementsForCount, calculateGeneticCoveragePercentage } from './utils/algorithms/geneticAlgorithm';
import { calculateRayTracingPlacementsForCount, calculateRayTracingCoveragePercentage } from './utils/algorithms/rayTracing';
import { calculateGradientDescentPlacementsForCount, calculateGradientDescentCoveragePercentage } from './utils/algorithms/gradientDescent';
import { calculateCoverageGapPlacementsForCount, calculateCoverageGapCoveragePercentage } from './utils/algorithms/coverageGap';

// UI Components
import AlgorithmSelector from './components/AlgorithmSelector';
import MeshNodeSelector from './components/MeshNodeSelector';
import VisualizationManager from './components/Visualization/VisualizationManager'; // Assuming this handles 3D
import CoverageAnalysis from './components/Results/CoverageAnalysis'; // Keep for displaying percentages

// Animation Variants (Keep if needed)
import { resultsModalVariants, contentVariants, buttonHoverVariants } from './utils/animations';

// --- Results Modal ---
const ResultsModal = ({ isOpen, onClose }) => {
  // --- Context ---
  const {
    allResults,
    formData,
    resetForm,
    selectedAlgorithmType,
    updateSelectedAlgorithmType,
    userNodeCount,
    updateUserNodeCount,
  } = useWirelessAdvisor();
  // --- End Context ---

  // --- State ---
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [recalculatedData, setRecalculatedData] = useState(null); // Holds { placements, coveragePercentages }
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [showInputSummary, setShowInputSummary] = useState(false); // Keep summary collapsed by default
  // --- End State ---

  const isMobile = windowWidth < 768;

  // --- Memoized Derived State ---
  const currentAlgorithmResult = useMemo(() => {
    if (!allResults || !selectedAlgorithmType) return null;
    return allResults.algorithmResults?.[selectedAlgorithmType];
  }, [allResults, selectedAlgorithmType]);

  // Determine recommended nodes for B/M/H for the *selected* algorithm
  const recommendedNodes = useMemo(() => {
    if (!currentAlgorithmResult?.coverageLevels) return { basic: null, medium: null, high: null };
    return {
      basic: currentAlgorithmResult.coverageLevels.basic?.units,
      medium: currentAlgorithmResult.coverageLevels.medium?.units,
      high: currentAlgorithmResult.coverageLevels.high?.units,
    };
  }, [currentAlgorithmResult]);

  // Determine the node count to actually *use* for display/recalculation
  // If user hasn't overridden, default to the 'medium' recommendation for the selected algorithm
  const activeNodeCount = useMemo(() => {
    return userNodeCount !== null ? userNodeCount : recommendedNodes.medium;
  }, [userNodeCount, recommendedNodes.medium]);

  // Check if the active count deviates from ANY recommendation for the current algorithm
  const isDeviating = useMemo(() => {
      if (userNodeCount === null || !recommendedNodes) return false; // Not deviating if using default
      return userNodeCount !== recommendedNodes.basic &&
             userNodeCount !== recommendedNodes.medium &&
             userNodeCount !== recommendedNodes.high;
  }, [userNodeCount, recommendedNodes]);

  // Determine which data set to display (pre-calculated or recalculated)
  const displayData = useMemo(() => {
    if (userNodeCount !== null && recalculatedData) {
      // Use recalculated data if user override is active and calculation is done
      return {
        placements: recalculatedData.placements,
        coveragePercentages: recalculatedData.coveragePercentages,
        nodeCountSource: 'recalculated',
      };
    } else if (currentAlgorithmResult?.coverageLevels) {
      // Determine which pre-calculated level matches the activeNodeCount (if any)
      let level = null;
      if (activeNodeCount === recommendedNodes.basic) level = 'basic';
      else if (activeNodeCount === recommendedNodes.medium) level = 'medium';
      else if (activeNodeCount === recommendedNodes.high) level = 'high';

      if (level) {
        // Use pre-calculated data for the matching level
        return {
          placements: currentAlgorithmResult.coverageLevels[level]?.placements,
          coveragePercentages: currentAlgorithmResult.coveragePercentages, // Use the percentages calculated initially for B/M/H
          nodeCountSource: `recommended-${level}`,
        };
      } else {
         // Should ideally be covered by recalculatedData, but as a fallback:
         // If activeNodeCount doesn't match B/M/H and recalculation hasn't finished/failed
         return {
             placements: [], // No placements to show yet
             coveragePercentages: { basic: 'N/A', medium: 'N/A', high: 'N/A' },
             nodeCountSource: 'unknown',
         }
      }
    }
    return null; // No data available
  }, [userNodeCount, recalculatedData, currentAlgorithmResult, activeNodeCount, recommendedNodes]);
  // --- End Memoized Derived State ---


  // --- Recalculation Effect ---
  useEffect(() => {
    // Only recalculate if userNodeCount is set (meaning user has interacted)
    if (userNodeCount !== null && selectedAlgorithmType && allResults) {
      const performRecalculation = async () => {
        setIsRecalculating(true);
        setRecalculatedData(null);
        console.log(`Recalculating for ${userNodeCount} nodes using ${selectedAlgorithmType}`);
        try {
          let placementsResult = [];
          let coverageResult = { basic: 0, medium: 0, high: 0 };
          // Add floorPlanLayout to the arguments passed to algorithms
          const args = [
              allResults.formData,
              allResults.routerStandard,
              allResults.routerSpecs,
              userNodeCount,
              allResults.floorPlanLayout // Pass the layout
          ];
          // Arguments needed for coverage percentage calculation (excluding unitCount)
          const coverageArgs = [
              placementsResult, // This will be updated below
              allResults.formData,
              allResults.routerSpecs,
              allResults.floorPlanLayout
          ];


          // Dynamically call the correct recalculation functions, passing floorPlanLayout
          switch (selectedAlgorithmType) {
            // case ALGORITHM_TYPES.SIMPLE: // Assuming simple doesn't exist or needs update
            //   placementsResult = calculateSimplePlacementsForCount(...args);
            //   coverageArgs[0] = placementsResult; // Update placements in coverageArgs
            //   coverageResult = calculateSimpleCoveragePercentage(userNodeCount, ...coverageArgs);
            //   break;
            case ALGORITHM_TYPES.GENETIC:
              placementsResult = calculateGeneticPlacementsForCount(...args);
              coverageArgs[0] = placementsResult;
              coverageResult = calculateGeneticCoveragePercentage(userNodeCount, ...coverageArgs);
              break;
            case ALGORITHM_TYPES.RAY_TRACING:
              placementsResult = calculateRayTracingPlacementsForCount(...args);
              coverageArgs[0] = placementsResult;
              coverageResult = calculateRayTracingCoveragePercentage(userNodeCount, ...coverageArgs);
              break;
            // case ALGORITHM_TYPES.WEIGHTED_ROOM: // Assuming weighted doesn't exist or needs update
            //   placementsResult = calculateWeightedRoomPlacementsForCount(...args);
            //   coverageArgs[0] = placementsResult;
            //   coverageResult = calculateWeightedRoomCoveragePercentage(userNodeCount, ...coverageArgs);
            //   break;
            case ALGORITHM_TYPES.GRADIENT_DESCENT:
              placementsResult = calculateGradientDescentPlacementsForCount(...args);
              coverageArgs[0] = placementsResult;
              coverageResult = calculateGradientDescentCoveragePercentage(userNodeCount, ...coverageArgs);
              break;
            case ALGORITHM_TYPES.COVERAGE_GAP:
              placementsResult = calculateCoverageGapPlacementsForCount(...args);
              coverageArgs[0] = placementsResult;
              coverageResult = calculateCoverageGapCoveragePercentage(userNodeCount, ...coverageArgs);
              break;
            default:
              console.warn(`Recalculation not implemented or invalid for algorithm type: ${selectedAlgorithmType}.`);
              // Fallback or error state
              placementsResult = [];
              coverageResult = { basic: 'N/A', medium: 'N/A', high: 'N/A' };
          }

          setRecalculatedData({
            placements: placementsResult || [],
            coveragePercentages: coverageResult || { basic: 0, medium: 0, high: 0 }
          });

        } catch (error) {
          console.error("Error during recalculation:", error);
          setRecalculatedData({ // Indicate error state
              placements: [],
              coveragePercentages: { basic: 'Error', medium: 'Error', high: 'Error' }
          });
        } finally {
          setIsRecalculating(false);
        }
      };
      performRecalculation();
    } else {
      // Reset if user clears override (userNodeCount becomes null)
      setRecalculatedData(null);
    }
  }, [userNodeCount, selectedAlgorithmType, allResults]); // Dependencies for recalculation
  // --- End Recalculation Effect ---


  // --- Event Handlers & Effects ---
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const handlePrint = () => window.print();
  const handleStartOver = () => {
    resetForm();
    onClose();
  };
  // --- End Event Handlers & Effects ---


  // --- Render Logic ---
  if (!isOpen) return null; // Don't render if not open

  // Loading state (if allResults not yet available after opening)
  if (!allResults || !selectedAlgorithmType) {
    return (
      <AnimatePresence>
        <motion.div /* Basic Loading Structure */ className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50">
           <div className="bg-white p-6 rounded-lg shadow-xl text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-700">Generating recommendations...</p>
           </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Main Modal Render
  return (
    <AnimatePresence>
      <>
        {/* Backdrop */}
        <motion.div
          className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm z-40"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Modal Container */}
        <motion.div
          className={`fixed top-0 bottom-0 left-0 bg-gray-100 shadow-2xl flex flex-col z-50 ${
            isMobile ? 'w-full' : 'w-full md:w-3/4 lg:w-2/3 xl:w-[70%]' // Responsive width
          }`}
          variants={resultsModalVariants} initial="hidden" animate="visible" exit="exit"
          aria-modal="true" role="dialog" aria-labelledby="results-modal-title"
        >
          {/* Header */}
          <motion.div
            className="p-4 border-b border-gray-300 flex justify-between items-center bg-white sticky top-0 z-10 flex-shrink-0"
            variants={contentVariants}
          >
            <div className="flex items-center">
              <Router className="h-5 w-5 text-purple-600 mr-2" />
              <h2 id="results-modal-title" className="text-lg md:text-xl font-semibold text-gray-800">Wireless Recommendation</h2>
            </div>
            <button
              className="text-gray-500 hover:text-gray-800 p-1 rounded-full hover:bg-gray-200 transition-colors"
              onClick={onClose} aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </motion.div>

          {/* Main Content (Layout: Controls on Left, Visualization on Right for Desktop) */}
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden">

            {/* Left Panel: Controls & Analysis (Scrollable) */}
            <div className="w-full md:w-1/2 lg:w-5/12 xl:w-1/3 flex-shrink-0 overflow-y-auto p-4 md:p-6 border-r border-gray-300 bg-white">

              {/* Algorithm Selection */}
              <div className="mb-6">
                <label htmlFor="algo-select" className="block text-sm font-medium text-gray-700 mb-2">Placement Algorithm</label>
                <AlgorithmSelector
                  id="algo-select"
                  selectedAlgorithm={selectedAlgorithmType}
                  onSelectAlgorithm={updateSelectedAlgorithmType}
                />
                {currentAlgorithmResult && (
                  <p className="mt-2 text-xs text-gray-500">
                    {currentAlgorithmResult.description}
                  </p>
                )}
              </div>

              {/* Node Count Adjustment */}
              <div className="mb-6">
                 <label className="block text-sm font-medium text-gray-700 mb-2">Number of Mesh Nodes</label>
                 {/* Display Recommendations */}
                 <div className="flex space-x-2 mb-3 text-xs">
                     <span className="text-gray-600">Recommended:</span>
                     <button onClick={() => updateUserNodeCount(recommendedNodes.basic)} className={`px-1.5 py-0.5 rounded ${activeNodeCount === recommendedNodes.basic && userNodeCount === null ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} disabled={recommendedNodes.basic === null}>{recommendedNodes.basic ?? '?'} (Basic)</button>
                     <button onClick={() => updateUserNodeCount(recommendedNodes.medium)} className={`px-1.5 py-0.5 rounded ${activeNodeCount === recommendedNodes.medium && userNodeCount === null ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} disabled={recommendedNodes.medium === null}>{recommendedNodes.medium ?? '?'} (Medium)</button>
                     <button onClick={() => updateUserNodeCount(recommendedNodes.high)} className={`px-1.5 py-0.5 rounded ${activeNodeCount === recommendedNodes.high && userNodeCount === null ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} disabled={recommendedNodes.high === null}>{recommendedNodes.high ?? '?'} (High)</button>
                 </div>
                 <MeshNodeSelector
                    nodeCount={activeNodeCount ?? 1} // Use the active count
                    onNodeCountChange={updateUserNodeCount}
                    minNodes={1}
                    maxNodes={8} // Max cap
                 />
                 {isRecalculating && <p className="text-xs text-purple-600 mt-1 animate-pulse">Recalculating...</p>}
              </div>

               {/* Deviation Warning */}
               {isDeviating && !isRecalculating && (
                 <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg text-sm text-yellow-800 flex items-start">
                   <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-yellow-600"/>
                   <span>You've selected {userNodeCount} nodes, which differs from the recommendations. Coverage is recalculated.</span>
                 </div>
               )}

              {/* Coverage Analysis */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center"><Wifi size={16} className="mr-1.5"/> Coverage Analysis</h3>
                {displayData ? (
                  <CoverageAnalysis
                    coveragePercentages={displayData.coveragePercentages}
                    speedEstimates={allResults.speedEstimates} // Assuming speed estimates don't change with node count
                    internetSpeed={formData.internetSpeed}
                    totalArea={formData.area} // Pass area if needed by component
                  />
                ) : (
                  <p className="text-sm text-gray-500">Calculating coverage...</p>
                )}
              </div>

              {/* Input Summary (Optional/Collapsible) */}
              <details className="mb-6 text-xs text-gray-600" open={showInputSummary} onToggle={(e) => setShowInputSummary(e.target.open)}>
                  <summary className="cursor-pointer font-medium hover:text-gray-800">View Input Summary</summary>
                  <div className="mt-2 p-3 bg-gray-50 rounded border border-gray-200 grid grid-cols-2 gap-x-4 gap-y-1">
                      <div><strong>Home Size:</strong> {formData.homeSize} ({formData.area}m²)</div>
                      <div><strong>Floors:</strong> {formData.floors}</div>
                      <div><strong>Internet:</strong> {formData.internetSpeed}</div>
                      <div><strong>Router Loc:</strong> {formData.routerLocation}</div>
                      <div><strong>Wall Density:</strong> {formData.wallDensity}</div>
                      <div><strong>Construction:</strong> {formData.constructionType}</div>
                  </div>
              </details>

            </div>

            {/* Right Panel: Visualization (Takes remaining space) */}
            <div className="flex-1 flex flex-col bg-gray-700 relative">
              {/* Visualization Header Info */}
              <div className="p-3 bg-gray-800 text-gray-300 text-xs flex justify-between items-center flex-shrink-0">
                  <span>Algorithm: <span className="font-medium text-gray-100">{getAlgorithmName(selectedAlgorithmType)}</span></span>
                  <span>Nodes: <span className="font-medium text-gray-100">{activeNodeCount ?? '?'} / {recommendedNodes.medium ?? '?'} (Rec.)</span></span> {/* Show active vs recommended */}
              </div>

              {/* Visualization Area */}
              <div className="flex-1 relative min-h-[300px] md:min-h-0">
                 {displayData && displayData.placements ? (
                     <VisualizationManager
                        formData={formData}
                        placements={displayData.placements} // Pass current placements
                        floorPlanLayout={allResults.floorPlanLayout} // Pass the layout
                        nodeCount={activeNodeCount ?? 0}
                        algorithm={getAlgorithmName(selectedAlgorithmType)}
                        routerStandard={allResults.routerStandard}
                        // Add a prop to force 3D view if needed, e.g., viewMode="3D"
                        // Ensure VisualizationManager prioritizes 3D view
                     />
                 ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                        {isRecalculating ? 'Generating visualization...' : 'Visualization not available.'}
                    </div>
                 )}
              </div>
            </div>

          </div> {/* End Main Content Flex */}

          {/* Footer Actions */}
          <motion.div
            className="p-3 border-t border-gray-300 bg-gray-100 flex flex-col sm:flex-row justify-end items-center space-y-2 sm:space-y-0 sm:space-x-3 flex-shrink-0"
            variants={contentVariants}
          >
            <motion.button
              className="w-full sm:w-auto px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors flex items-center justify-center"
              onClick={handlePrint}
              variants={buttonHoverVariants} initial="initial" whileHover="hover" whileTap="tap"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Results
            </motion.button>
            <motion.button
              className="w-full sm:w-auto px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center justify-center"
              onClick={handleStartOver}
              variants={buttonHoverVariants} initial="initial" whileHover="hover" whileTap="tap"
            >
              <Layers className="h-4 w-4 mr-2" />
              Start Over
            </motion.button>
          </motion.div>

        </motion.div> {/* End Modal Container */}
      </>
    </AnimatePresence>
  );
};

export default ResultsModal;